import { SearchOutlined } from "@ant-design/icons";
import { Col, Form, Input, message, Row, Select, Spin } from "antd";
import { CALL_API } from "common/API";
import { REPORT_DURATION_TYPES, STATUS_CODE } from "common/Constants";
import { includes, uniq } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import { getClientid, getUserInfo } from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import {
  DateFilterForm,
  ENCASHMENT_STATUS,
  formatAmount,
  formatDate,
  formatDays,
  getDateFilterRange,
  PROCESSING_STATUS_LIST,
  ProcessingStatusTag,
  StyledFilterRow,
  StyledTable,
} from "./common";

function LeaveEncashmentReport({ ClientId, userInfo }) {
  const [form] = Form.useForm();
  const [encashmentList, setEncashmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [payrollPeriod, setPayrollPeriod] = useState("");
  const [processingStatus, setProcessingStatus] = useState("");
  const [CSVData, setCSVData] = useState([]);
  const csvLinkEl = useRef();
  const containerRef = useRef(null);

  const isHR = userInfo?.higherOfficialsList?.some(
    (official) => official.role === "HR" && official.email === userInfo.userid
  );

  const getEncashmentReport = async () => {
    setLoading(true);
    const { code, encashments = [] } = await CALL_API(
      `leave-encashment-list/${ClientId}`,
      "post",
      {
        status_id: "",
        ...getDateFilterRange(form),
        baselocationid: isHR ? userInfo?.baseLocationId : "",
      }
    );
    setLoading(false);
    if (includes([STATUS_CODE.SUCCESS, STATUS_CODE.RECORD_NOT_FOUND], code)) {
      setEncashmentList(encashments);
    } else {
      setEncashmentList([]);
    }
  };

  useEffect(() => {
    if (ClientId) getEncashmentReport();
  }, [ClientId]);

  // Rejected requests are never paid, so they don't belong in the payroll report.
  const reportList = useMemo(
    () =>
      encashmentList.filter(
        (item) => item?.status_id !== ENCASHMENT_STATUS.REJECTED
      ),
    [encashmentList]
  );

  const payrollPeriodOptions = [
    { value: "", label: "All Payroll Periods" },
    ...uniq(reportList.map((item) => item?.payroll_period).filter(Boolean)).map(
      (period) => ({ value: period, label: period })
    ),
  ];

  const filteredList = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    return reportList
      .filter((item) => !payrollPeriod || item?.payroll_period === payrollPeriod)
      .filter(
        (item) =>
          !processingStatus ||
          (item?.processing_status || "Pending") === processingStatus
      )
      .filter(
        (item) =>
          !searchText ||
          item?.fullname?.toLowerCase().includes(searchText) ||
          item?.employeeid?.toLowerCase().includes(searchText)
      )
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [reportList, payrollPeriod, processingStatus, search]
  );

  const columns = [
    { title: "Name", dataIndex: "fullname", key: "fullname" },
    { title: "Employee ID", dataIndex: "employeeid", key: "employeeid" },
    {
      title: "Date Applied",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDate,
    },
    {
      title: "Date Approved",
      dataIndex: "approved_at",
      key: "approved_at",
      render: formatDate,
    },
    { title: "Leave Type", dataIndex: "leave_category_name", key: "leave_category_name" },
    {
      title: "Days Encashed",
      dataIndex: "approved_days",
      key: "approved_days",
      render: (_, record) =>
        formatDays(record?.approved_days || record?.number_of_days),
    },
    {
      title: "Daily Rate",
      dataIndex: "daily_rate",
      key: "daily_rate",
      render: (rate, record) => formatAmount(rate, record?.currency),
    },
    {
      title: "Amount Paid",
      dataIndex: "amount_paid",
      key: "amount_paid",
      render: (amount, record) => formatAmount(amount, record?.currency),
    },
    {
      title: "Payroll Period",
      dataIndex: "payroll_period",
      key: "payroll_period",
      render: (period) => period || "-",
    },
    {
      title: "Processing Date",
      dataIndex: "processing_date",
      key: "processing_date",
      render: formatDate,
    },
    {
      title: "Processing Status",
      dataIndex: "processing_status",
      key: "processing_status",
      render: (status) => <ProcessingStatusTag status={status || "Pending"} />,
    },
  ];

  const CSVHeaders = [
    "Name",
    "Employee ID",
    "Date Applied",
    "Date Approved",
    "Leave Type",
    "Days Encashed",
    "Daily Rate",
    "Amount Paid",
    "Payroll Period",
    "Processing Date",
    "Processing Status",
  ].map((label) => ({ label, key: label }));

  const prepareCSV = () => {
    if (!filteredList.length) {
      message.error("No data available to download.");
      return;
    }
    setCSVData(
      filteredList.map((item) => ({
        Name: item?.fullname,
        "Employee ID": item?.employeeid,
        "Date Applied": formatDate(item?.created_at),
        "Date Approved": formatDate(item?.approved_at),
        "Leave Type": item?.leave_category_name,
        "Days Encashed": item?.approved_days || item?.number_of_days,
        "Daily Rate": item?.daily_rate ?? "",
        "Amount Paid": item?.amount_paid ?? "",
        "Payroll Period": item?.payroll_period || "",
        "Processing Date": formatDate(item?.processing_date),
        "Processing Status": item?.processing_status || "Pending",
      }))
    );
    setTimeout(() => csvLinkEl.current.link.click());
  };

  return (
    <>
      <Row className="page-title" ref={containerRef}>
        <Col xs={24} lg={20}>
          <StyledFilterRow>
            <Input
              className="search"
              allowClear
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <DateFilterForm
              form={form}
              name="leave_encashment_report_date_form"
              durationOptions={REPORT_DURATION_TYPES}
              initialDurationType={2}
              onChange={getEncashmentReport}
              containerRef={containerRef}
            />
            <Select
              value={payrollPeriod}
              options={payrollPeriodOptions}
              onChange={setPayrollPeriod}
              getPopupContainer={() => containerRef.current}
            />
            <Select
              value={processingStatus}
              options={[
                { value: "", label: "All Processing Status" },
                ...PROCESSING_STATUS_LIST,
              ]}
              onChange={setProcessingStatus}
              getPopupContainer={() => containerRef.current}
            />
          </StyledFilterRow>
        </Col>
        <Col xs={24} lg={4} className="align-right">
          <ButtonOutlined onClick={prepareCSV}>Export CSV</ButtonOutlined>
          <CSVLink
            headers={CSVHeaders}
            data={CSVData}
            filename="leave-encashment-report.csv"
            ref={csvLinkEl}
          />
        </Col>
      </Row>
      <Spin spinning={loading}>
        <StyledTable
          dataSource={filteredList}
          columns={columns}
          rowKey="encashment_id"
          rowClassName={(_, index) => (index % 2 === 0 ? "" : "non-white")}
          pagination={filteredList.length > 100 ? { pageSize: 100 } : false}
        />
      </Spin>
    </>
  );
}

const mapStateToProps = (state) => ({
  ClientId: getClientid(state),
  userInfo: getUserInfo(state),
});

export default connect(mapStateToProps, null)(LeaveEncashmentReport);
